import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbCopy, TbLink } from "react-icons/tb";
import { ValutDialog } from "../../../../../Components/Molecules/ValutDialog";
import {
  createBaasEntityKycInvite,
  createBaasKycOnboardInvite,
} from "../../../../../Services/baasEntidadesApi";

const DATA_SCOPES = [
  { id: "personal", labelKey: "baas.entidades.detail.verifications.generateScopePersonal" },
  { id: "document", labelKey: "baas.entidades.detail.verifications.generateScopeDocument" },
  { id: "address", labelKey: "baas.entidades.detail.verifications.generateScopeAddress" },
];

const emptySelection = () => ({
  personal: false,
  document: false,
  address: false,
  liveness: false,
});

const resolveProxyErrorMessage = (error) => {
  const detail =
    error?.detail ||
    error?.response?.detail ||
    error?.body?.detail ||
    error?.data?.detail ||
    error?.message;
  if (detail && String(detail).trim()) return String(detail).trim();
  return intl.get("baas.entidades.detail.verifications.inviteError");
};

const buildInviteUrl = (pathOrToken) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = String(pathOrToken || "").startsWith("/")
    ? pathOrToken
    : `/auth/onboard/${pathOrToken}`;
  return `${origin}${path}`;
};

const describeSelection = (selection) => {
  const data = DATA_SCOPES.filter((item) => selection[item.id]).map((item) =>
    intl.get(item.labelKey),
  );
  const parts = [];
  if (data.length) parts.push(data.join(", "));
  if (selection.liveness) {
    parts.push(intl.get("baas.entidades.detail.verifications.generateScopeLiveness"));
  }
  return parts.join(" · ");
};

/**
 * Public KYC generate modal (`/auth/onboard/{token}`).
 * - No entityId: blank invite (new person completes signup + Veriff).
 * - With entityId: revalidation scopes and/or liveness Veriff.
 * Does not create a Veriff session in PaySmart documents.
 */
export const BaasEntidadeKycGenerateModal = ({
  open,
  onClose,
  instance,
  entityId,
  canCreateInvite,
  inviteFeatureEnabled,
  onGenerated,
  onViewAllLinks,
}) => {
  const [selection, setSelection] = useState(emptySelection);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [inviteName, setInviteName] = useState("");

  const isBlankInvite = !entityId;
  const updateScopes = DATA_SCOPES.filter((item) => selection[item.id]).map((item) => item.id);
  const hasData = updateScopes.length > 0;
  const hasLiveness = Boolean(selection.liveness);
  const canSubmit =
    !busy &&
    Boolean(canCreateInvite && inviteFeatureEnabled && instance) &&
    (isBlankInvite || hasData || hasLiveness);

  const typeLabel = useMemo(() => describeSelection(selection), [selection]);

  const reset = () => {
    setSelection(emptySelection());
    setBusy(false);
    setError("");
    setResult(null);
    setCopied(false);
    setInviteName("");
  };

  useEffect(() => {
    if (open) {
      reset();
    }
    // Fresh form each time the dialog opens (blank vs revalidation).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const toggle = (key) => {
    setSelection((current) => ({ ...current, [key]: !current[key] }));
    setError("");
  };

  const handleGenerate = async () => {
    if (!canSubmit || !instance) return;
    if (!isBlankInvite && !hasData && !hasLiveness) {
      setError(intl.get("baas.entidades.detail.verifications.generateScopeRequired"));
      return;
    }

    setBusy(true);
    setError("");
    try {
      const invite = isBlankInvite
        ? await createBaasKycOnboardInvite(instance, {
            expiresInHours: 168,
            name: inviteName,
          })
        : await createBaasEntityKycInvite(instance, entityId, {
            expiresInHours: 168,
            updateScopes,
            includeLiveness: hasLiveness,
          });

      const inviteUrl = buildInviteUrl(
        invite.path || invite.Path || invite.token || invite.Token,
      );
      setResult({
        invite,
        inviteUrl,
        copyUrl: inviteUrl,
        typeLabel: describeSelection(selection),
      });

      if (typeof onGenerated === "function") {
        await onGenerated({ invite });
      }
    } catch (err) {
      const code = String(err?.error || err?.code || err?.response?.error || "").toLowerCase();
      setError(
        code === "feature_disabled"
          ? intl.get("baas.entidades.detail.verifications.inviteEnableHint")
          : resolveProxyErrorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    const url = result?.copyUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <ValutDialog
      open={open}
      onClose={handleClose}
      title={
        result
          ? intl.get("baas.entidades.detail.verifications.generateDoneTitle")
          : isBlankInvite
            ? intl.get("baas.entidades.detail.verifications.generateBlankTitle")
            : intl.get("baas.entidades.detail.verifications.generateTitle")
      }
      icon={<TbLink className="h-4 w-4" />}
      maxWidth="sm"
      disableBackdropClose={busy}
      footer={
        result ? (
          <>
            <button type="button" className="btn-outline w-full sm:w-auto" onClick={handleClose}>
              {intl.get("common.close") || intl.get("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary w-full sm:w-auto"
              onClick={handleCopy}
              disabled={!result.copyUrl}
            >
              <span className="inline-flex items-center gap-1.5">
                <TbCopy className="h-4 w-4" aria-hidden />
                {copied
                  ? intl.get("baas.entidades.detail.verifications.copied")
                  : intl.get("baas.entidades.detail.verifications.copyLink")}
              </span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn-outline w-full sm:w-auto"
              onClick={handleClose}
              disabled={busy}
            >
              {intl.get("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary w-full sm:w-auto"
              onClick={() => {
                void handleGenerate();
              }}
              disabled={!canSubmit}
            >
                {busy
                ? intl.get("baas.entidades.detail.verifications.generateBusy")
                : isBlankInvite
                  ? intl.get("baas.entidades.detail.verifications.generateBlankButton")
                  : intl.get("baas.entidades.detail.verifications.generateButton")}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {isBlankInvite
              ? intl.get("baas.entidades.detail.verifications.generateBlankDoneHint")
              : intl.get("baas.entidades.detail.verifications.generateDoneHint")}
          </p>
          {isBlankInvite ? null : (
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              {result.typeLabel || typeLabel}
            </p>
          )}
          {result.copyUrl ? (
            <p className="break-all font-mono text-xs text-gray-500 dark:text-gray-400">
              {result.copyUrl}
            </p>
          ) : null}
          {typeof onViewAllLinks === "function" ? (
            <button
              type="button"
              className="text-sm font-medium text-[#b8860b] underline-offset-2 hover:underline"
              onClick={() => onViewAllLinks()}
            >
              {intl.get("baas.entidades.detail.verifications.generateViewAllLinks")}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {isBlankInvite
              ? intl.get("baas.entidades.detail.verifications.generateBlankHint")
              : intl.get("baas.entidades.detail.verifications.generateHint")}
          </p>
          {isBlankInvite ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {intl.get("baas.entidades.quickActions.onboardInviteNameLabel")}
              </span>
              <input
                type="text"
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder={intl.get("baas.entidades.quickActions.onboardInviteNamePlaceholder")}
                maxLength={128}
                disabled={busy || !canCreateInvite || !inviteFeatureEnabled}
                autoComplete="off"
              />
            </label>
          ) : null}
          {isBlankInvite && typeof onViewAllLinks === "function" ? (
            <button
              type="button"
              className="text-sm font-medium text-[#b8860b] underline-offset-2 hover:underline"
              onClick={() => onViewAllLinks()}
            >
              {intl.get("baas.entidades.detail.verifications.generateViewAllLinks")}
            </button>
          ) : null}

          {isBlankInvite ? null : (
            <>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {intl.get("baas.entidades.detail.verifications.generateDataTitle")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {DATA_SCOPES.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      disabled={!canCreateInvite || !inviteFeatureEnabled || busy}
                      aria-pressed={selection[item.id]}
                      onClick={() => toggle(item.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        selection[item.id]
                          ? "border-[#e7a73f] bg-[#e7a73f]/10 text-[#b8860b]"
                          : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-300"
                      } disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                      {intl.get(item.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-start gap-2 rounded-xl border border-gray-100 p-3 dark:border-brand-dark-border">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={selection.liveness}
                  disabled={busy || !canCreateInvite || !inviteFeatureEnabled}
                  onChange={() => toggle("liveness")}
                />
                <span>
                  <span className="block text-sm font-medium text-brand-dark dark:text-gray-100">
                    {intl.get("baas.entidades.detail.verifications.generateScopeLiveness")}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                    {inviteFeatureEnabled
                      ? intl.get("baas.entidades.detail.verifications.generateLivenessHint")
                      : intl.get("baas.entidades.detail.verifications.inviteEnableHint")}
                  </span>
                </span>
              </label>
            </>
          )}

          {!inviteFeatureEnabled && canCreateInvite ? (
            <p className="text-xs text-gray-500">
              {intl.get("baas.entidades.detail.verifications.inviteEnableHint")}
            </p>
          ) : null}

          {error ? (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </ValutDialog>
  );
};

export default BaasEntidadeKycGenerateModal;
